import {Alert} from 'react-native';

import {ScheduledOrderPreviewLine} from '../services/ScheduledProposalService/ScheduledProposalService';
import {useBagStore} from '../store/BagStore';
import {
  SacolaPaymentMode,
  useScheduledProposalStore,
} from '../store/ScheduledProposalStore';

/**
 * Monta a sacola a partir de um carrinho já resolvido pelo servidor e prepara o
 * vínculo que o checkout vai mandar no header `X-Proposal-Id`.
 *
 * ## Por que isto vive fora das telas
 *
 * Nasceu dentro de `ScheduledOrderReview` e passou a ser necessário, palavra
 * por palavra, na tela do PEDIDO A PAGAR (PAG-01). São ~40 linhas em que cada
 * uma é uma decisão — substituir em vez de mesclar, usar o produto do servidor
 * em vez de remontar por SKU, a `cartKey` promocional — e duas cópias delas
 * divergiriam na primeira correção feita só de um lado. O sintoma seria um
 * pedido finalizado diferente do carrinho aprovado, e só numa das duas telas.
 *
 * ## O produto vem do SERVIDOR, nunca do catálogo local
 *
 * `linha.produto` é o mesmo item que o backend acabou de resolver e ajustar
 * contra o estoque. Remontar por SKU a partir do catálogo do app abriria a
 * porta para finalizar um pedido diferente daquele que o cliente aprovou na
 * tela anterior — com outro preço, outra tabela ou outra quantidade.
 *
 * ## A sacola é SUBSTITUÍDA, e não somada
 *
 * Aceitar (ou pagar) é dizer sim ao que o vendedor montou. Mesclar com o que já
 * estava na sacola faria o cliente pagar por itens que ele não está revisando
 * naquela tela — e, no à vista, itens que o vendedor não lançou entrariam numa
 * cobrança que o Conecta Sales espera conferir linha a linha.
 *
 * @returns `true` quando a sacola foi montada e o chamador pode navegar.
 */
export const montarSacolaDaProposta = (
  linhas: ScheduledOrderPreviewLine[],
  orderApplicationId: string,
  referenceDate: string,
  paymentMode: SacolaPaymentMode = 'A_PRAZO',
): boolean => {
  const sobreviventes = linhas.filter(
    linha => linha.acao !== 'REMOVIDO' && linha.quantidadeAjustada > 0,
  );

  if (!sobreviventes.length) {
    Alert.alert(
      'Não conseguimos montar seu pedido',
      'Nenhum item deste pedido está disponível no momento. Fale com seu vendedor.',
    );
    return false;
  }

  // Sem o produto resolvido não há o que montar — e montar "quase certo" seria
  // pior que não montar.
  if (sobreviventes.some(linha => !linha.produto?.sku)) {
    Alert.alert(
      'Não conseguimos montar seu pedido',
      'Tente novamente em alguns instantes.',
    );
    return false;
  }

  const {clearCart, addProduct} = useBagStore.getState();

  clearCart();

  sobreviventes.forEach(linha => {
    const produto = linha.produto;
    addProduct({
      ...produto,
      quantity: linha.quantidadeAjustada,
      cartKey: produto.is_promotion ? `${produto.sku}_promo` : produto.sku,
    });
  });

  // O vínculo é com a ORDEM, não com o conjunto: é `sent_orders.proposal_id`
  // que faz o pedido nascer correlacionado àquela venda, e é ele que o checkout
  // manda no header `X-Proposal-Id`. No à vista é também o que o backend usa
  // para saber que a sacola é de um pedido a pagar — e daí para recusar boleto
  // e cartão, pular a trava de duplicidade e gravar a cobrança na ordem.
  useScheduledProposalStore
    .getState()
    .setProposal(orderApplicationId, referenceDate, paymentMode);

  return true;
};
